import { PageBody, PageBodyStepper } from "@Light/components/page/PageBody";
import { useAccount } from "@Light/components/page/account";
import type {
  AddressSearchResponse,
  AddressValue,
} from "@Light/services/lightTypes";
import { formatAddress, isFullAddress, zip5 } from "@Light/utils/address";
import { Lightning01, MarkerPin04 } from "@untitledui/icons";
import { EnrollHelpButtonFooter } from "../EnrollHelpButtonFooter";
import { PartnerTosDisclaimer } from "../PartnerTosDisclaimer";
import { AddressSearchField } from "../address/AddressSearchField";
import { ServiceLocationSheets } from "../address/ServiceLocationSheets";
import { type UseAddressSearch } from "../address/useAddressSearch";
import { useServiceLocationSheets } from "../address/useServiceLocationSheets";
import { useEnrollment } from "../flow/context";
import { useEnrollmentStepper } from "../flow/useEnrollmentStepper";
import { useIsAfterRate } from "../flow/useIsAfterRate";
import { EnrollRoute } from "../routes";
import { useBrokerTermsAgreement } from "../useBrokerTermsAgreement";

export const PickAddressIntro = () => {
  const account = useAccount();
  const { requirements, back } = useEnrollment();
  const isAfterRate = useIsAfterRate();
  const stepper = useEnrollmentStepper(EnrollRoute.PickAddressIntro);

  // When preliminary address was provided, but we couldn't match to a utility
  // account number. Only show if they didn't change to a different zip earlier.
  const preliminary = account.preliminary_address;
  const searchedZip = requirements.service_address?.value?.postal_code;
  const unmatchedAddress =
    isFullAddress(preliminary) &&
    !requirements.utility_account_number?.value &&
    (!searchedZip || zip5(preliminary.postal_code) === zip5(searchedZip))
      ? preliminary
      : undefined;

  const brokerTermsAgreement = useBrokerTermsAgreement(!isAfterRate);
  const sheets = useServiceLocationSheets({
    postalCode: requirements?.service_address?.value?.postal_code,
    additionalRequirements: brokerTermsAgreement,
    // Address search is managed in the screen, not via sheet
    addressSearchSheet: false,
  });

  return (
    <>
      <BasePickAddressIntro
        {...sheets.addressSearch}
        isAfterRate={isAfterRate}
        unmatchedAddress={unmatchedAddress}
        onSelect={sheets.selectResult}
        onOpenUtilityAccountNumber={() => {
          sheets.openSheet("uan");
        }}
        onBack={back}
        stepper={stepper}
      />
      <ServiceLocationSheets {...sheets} />
    </>
  );
};

export type BasePickAddressIntroProps = UseAddressSearch & {
  unmatchedAddress?: AddressValue;
  isAfterRate?: boolean;
  stepper?: PageBodyStepper;
  onSelect?: (result: AddressSearchResponse) => void;
  onOpenUtilityAccountNumber?: () => void;
  onBack?: () => void;
  /** Render the address list open (only used by stories) */
  forceOpen?: boolean;
};

/** Overrideable base component for `PickAddressIntro`, for storybook use only. */
export const BasePickAddressIntro = ({
  unmatchedAddress,
  isAfterRate,
  stepper,
  forceOpen,
  onSelect,
  onOpenUtilityAccountNumber,
  onBack,
  ...search
}: BasePickAddressIntroProps) => {
  return (
    <PageBody
      stepper={stepper}
      centered
      icon={isAfterRate ? MarkerPin04 : Lightning01}
      title={
        isAfterRate ? (
          <>
            We just need a few details.
            <br />
            First, what&rsquo;s your address?
          </>
        ) : (
          "Let’s find your electricity plan. What’s your address?"
        )
      }
      subtitle={
        unmatchedAddress
          ? `We have ${formatAddress(unmatchedAddress)} for you, but can’t find an eligible meter.`
          : undefined
      }
      footer={
        <div className="flex flex-col gap-3">
          <EnrollHelpButtonFooter />
          {!isAfterRate && <PartnerTosDisclaimer className="text-center" />}
        </div>
      }
      onBack={onBack}
    >
      <div className="pb-10">
        <AddressSearchField
          {...search}
          onSelect={(result) => onSelect?.(result)}
          onNotFound={() => onOpenUtilityAccountNumber?.()}
          forceOpen={forceOpen}
          optionsClassName={unmatchedAddress ? "max-h-66" : ""}
        />
        <div className="pt-1.5">
          <p className="text-sm text-tertiary">
            Know your ESI ID?{" "}
            <button
              onClick={() => {
                onOpenUtilityAccountNumber?.();
              }}
              type="button"
              data-testid="service-address-search-use-utility-account-number"
              className="cursor-pointer underline underline-offset-4"
            >
              Use ESI ID instead
            </button>
          </p>
        </div>
      </div>
    </PageBody>
  );
};
